"use client";

import { useRef, useState, type DragEvent } from "react";
import { requestJson } from "@/lib/clientRequest";
import type { SidebarFolder, SidebarWorker } from "./types";

const WORKER_DRAG_TYPE = "application/x-caw-worker";

/** Sidebar moves only change categorization, never the worker's configuration. */
export function useWorkerMoving({ workers, folders, onMoved, expand }: {
  workers: SidebarWorker[];
  folders: SidebarFolder[];
  onMoved?: () => Promise<void>;
  expand: (id: string) => void;
}) {
  const [dragged, setDragged] = useState<string | null>(null);
  const [over, setOver] = useState<string | null | undefined>(undefined);
  const [moving, setMoving] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const inFlight = useRef(false);
  const end = () => { setDragged(null); setOver(undefined); };

  async function move(workerId: string, folderId: string | null) {
    end();
    const worker = workers.find(w => w.id === workerId);
    const folder = folderId ? folders.find(f => f.id === folderId) : null;
    if (!worker || (folderId && (!folder || folder.businessId !== worker.businessId)) || inFlight.current) return;
    if ((worker.folderId ?? null) === folderId) return;
    inFlight.current = true;
    setMoving(workerId);
    setError(null);
    try {
      await requestJson(`/api/workers/${workerId}`, {
        method: "PATCH", body: { folderId }, fallback: "Could not move worker",
      });
      if (folderId) expand(folderId);
      window.dispatchEvent(new CustomEvent("caw:activity-refresh", { detail: { workerId } }));
      await onMoved?.();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Could not move worker");
    } finally {
      inFlight.current = false;
      setMoving(null);
    }
  }

  const workerProps = (worker: SidebarWorker) => ({
    draggable: !moving,
    onDragStart: (event: DragEvent<HTMLAnchorElement>) => {
      event.dataTransfer.setData(WORKER_DRAG_TYPE, worker.id);
      event.dataTransfer.effectAllowed = "move";
      setDragged(worker.id);
      setError(null);
    },
    onDragEnd: end,
  });
  const accepts = (event: DragEvent) => !moving && Boolean(dragged || Array.from(event.dataTransfer.types).includes(WORKER_DRAG_TYPE));
  const folderProps = (folderId: string | null) => ({
    "data-drop-active": over === folderId,
    onDragOver: (event: DragEvent<HTMLDivElement>) => {
      if (!accepts(event)) return;
      event.preventDefault();
      event.stopPropagation();
      event.dataTransfer.dropEffect = "move";
      setOver(folderId);
    },
    onDragLeave: (event: DragEvent<HTMLDivElement>) => {
      if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setOver(undefined);
    },
    onDrop: (event: DragEvent<HTMLDivElement>) => {
      if (!accepts(event)) return;
      event.preventDefault();
      event.stopPropagation();
      const workerId = event.dataTransfer.getData(WORKER_DRAG_TYPE) || dragged;
      if (workerId) void move(workerId, folderId);
      else end();
    },
  });
  return { workers, folders, dragged, moving, error, move, workerProps, folderProps };
}
export type WorkerMovement = ReturnType<typeof useWorkerMoving>;
